import { Box } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchFormDraft,
  fetchOrganizationName,
  fetchSubmissionContext,
  fetchSubmittingAuthorOnlyCopyrightForm
} from 'src/api/queries';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { CopyrightForm } from './components/CopyrightForm/CopyrightForm';
import { CopyrightFormSubmitted } from './components/CopyrightForm/CopyrightFormSubmitted';
import { ICopyrightForm, IRoleType } from '@publishing-engine/domain/models';
import SubmittingAuthorOnlyCopyrightForm from './components/CopyrightForm/SubmittingAuthorOnlyCopyrightForm';
import { SubmittingAuthorOnlyCopyrightFormSubmitted } from './components/CopyrightForm/SubmittingAuthorOnlyCopyrightFormSubmitted';

interface Props {
  isEditingAllowed: boolean;
}

export const CopyRightsTab: React.FC<Props> = ({ isEditingAllowed }) => {
  const {
    fieldsResponse,
    workflowResponse,
    submissionId,
    submissionContextId
  } = useSubmissionData();

  const {
    workflowData: { copyrightForm }
  } = workflowResponse;

  const isCopyrightSubmitted = copyrightForm?.submitted ?? false;
  const isCopyrightRequested = copyrightForm?.requested ?? false;

  const organizationNameQueryKey = [
    'organization-name',
    fieldsResponse.organizationId
  ];
  const { data: organizationNameData } = useQuery({
    queryKey: organizationNameQueryKey,
    queryFn: () =>
      fetchOrganizationName(fieldsResponse?.organizationId, 'author')
  });

  const copyrightFormQueryKey = [
    'submitting-author-only-copyright-form',
    { submissionId, roleType: 'author' }
  ];

  const { data: submittingAuthorOnlyCopyrightForm, refetch } = useQuery({
    queryKey: copyrightFormQueryKey,
    queryFn: () =>
      fetchSubmittingAuthorOnlyCopyrightForm(
        submissionId,
        'author' as IRoleType
      )
  });

  const isSubmittingAuthorOnlyCopyrightSubmitted =
    submittingAuthorOnlyCopyrightForm?.authorCopyrightForm.length !== 0;

  const { data, isLoading } = useQuery({
    queryKey: ['submission-context', submissionContextId],
    queryFn: () =>
      submissionContextId ? fetchSubmissionContext(submissionContextId) : null
  });

  const { data: copyrightFormDraft, isLoading: isLoadingCopyrightFormDraft } =
    useQuery({
      queryKey: [
        'form-draft',
        submissionId,
        'submission',
        'authorCopyrightSubmission'
      ],
      queryFn: () =>
        submissionId
          ? fetchFormDraft(
              submissionId,
              'submission',
              'authorCopyrightSubmission'
            )
          : undefined
    });

  const authorCopyrightModel =
    data && !isLoading ? data.config.authorCopyrightModel : null;

  const isOldCopyrightForm =
    authorCopyrightModel === 'SubmittingAuthorOnly' ||
    authorCopyrightModel === 'SubmittingAuthorForAll';

  if (isLoadingCopyrightFormDraft) {
    return null;
  }

  return (
    <Box>
      {isCopyrightSubmitted && !isOldCopyrightForm && (
        <CopyrightFormSubmitted organizationName={organizationNameData?.name} />
      )}
      {(!isCopyrightSubmitted || isCopyrightRequested) &&
        !isOldCopyrightForm && (
          <CopyrightForm
            organizationData={organizationNameData}
            draftCopyrightForm={copyrightFormDraft?.data as ICopyrightForm}
            isEditingAllowed={isEditingAllowed}
          />
        )}

      {authorCopyrightModel === 'SubmittingAuthorOnly' &&
      data?.config &&
      !isSubmittingAuthorOnlyCopyrightSubmitted ? (
        <SubmittingAuthorOnlyCopyrightForm
          organizationData={organizationNameData}
          config={data.config}
          refetch={refetch}
        />
      ) : (
        <SubmittingAuthorOnlyCopyrightFormSubmitted
          organizationName={organizationNameData?.name}
          submittingAuthorOnlyCopyrightForm={
            submittingAuthorOnlyCopyrightForm?.authorCopyrightForm[0]
          }
        />
      )}
    </Box>
  );
};
